/**
 * Inline editor for a single care-time row inside `ActivityLogModal`.
 *
 * Renders the same fields as `CareTimeQuickAddModal` (date, method,
 * minutes, notes) but inline — no stacked modal — so the user can save
 * (✓) or cancel (✗) without leaving the activity log. Matches D4: the
 * pencil reveals every editable field at once.
 *
 * @agentic connections
 */

import { useState } from "react";
import {
  ActionIcon,
  Anchor,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { showErrorNotification } from "@benchmark/ui/utils";
import { IconCheck, IconX } from "@tabler/icons-react";
import type { Observation } from "@medplum/fhirtypes";
import {
  CARE_TIME_METHOD_OPTIONS,
  isCareTimeNonBillable,
  type CareTimeMethod,
} from "@benchmark/core/constants";
import { isAutoGeneratedCareTime } from "@benchmark/core/resolvers";
import { useMedplum } from "@medplum/react";
import { createCareTimeFacade } from "@/facades/care-time.facade";

/** Props for `CareTimeEntryRowEditor`. @agentic connections */
export type CareTimeEntryRowEditorProps = {
  observation: Observation;
  patientId: string;
  patientDisplay: string;
  performerRoleId: string;
  performerDisplay: string;
  onCancel: () => void;
  onSaved?: (updated: Observation) => void;
  onRemoved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
};

const todayDate = (): string => new Date().toISOString().slice(0, 10);

const initialDate = (observation: Observation): string => {
  const iso =
    observation.effectiveDateTime ?? observation.effectivePeriod?.start;
  return iso?.slice(0, 10) ?? todayDate();
};

const initialMethod = (observation: Observation): CareTimeMethod => {
  const code = observation.method?.coding?.[0]?.code as
    CareTimeMethod | undefined;
  if (code && CARE_TIME_METHOD_OPTIONS.some((o) => o.code === code))
    return code;
  return "patient-profile-quick-add";
};

/**
 * Activity options offered by the inline editor. `call`/`sms` are stamped only
 * by the Dialpad tee and are never a manual choice — they're included only when
 * they are the entry's current method (so a legacy/auto value still renders).
 * @agentic connections
 */
export const buildEditorMethodOptions = (
  currentMethod: CareTimeMethod,
): { value: string; label: string }[] =>
  CARE_TIME_METHOD_OPTIONS.filter(
    (o) => (o.code !== "call" && o.code !== "sms") || o.code === currentMethod,
  ).map((o) => ({ value: o.code, label: o.display }));

/**
 * Inline editor that swaps a CareTimeEntryRow into edit mode. The
 * pencil affordance on the row toggles this in/out.
 * @agentic connections
 */
export const CareTimeEntryRowEditor = ({
  observation,
  patientId,
  patientDisplay,
  performerRoleId,
  performerDisplay,
  onCancel,
  onSaved,
  onRemoved,
  onDirtyChange,
}: CareTimeEntryRowEditorProps) => {
  const medplum = useMedplum();
  const [date, setDate] = useState<string>(initialDate(observation));
  const [method, setMethod] = useState<CareTimeMethod>(
    initialMethod(observation),
  );
  const [minutes, setMinutes] = useState<number | undefined>(
    observation.valueQuantity?.value,
  );
  const [notes, setNotes] = useState<string>(observation.note?.[0]?.text ?? "");
  const [nonBillable, setNonBillable] = useState<boolean>(
    isCareTimeNonBillable(observation),
  );
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);

  const markDirty = () => onDirtyChange?.(true);

  // `call`/`sms` are stamped only by the Dialpad tee — never offered as a
  // manual choice, so the auto/manual provenance stays trustworthy. They're
  // still shown (disabled) for an existing auto entry so its method renders.
  const isAuto = isAutoGeneratedCareTime(observation);
  const methodOptions = buildEditorMethodOptions(method);

  const handleSave = async () => {
    if (!minutes || minutes < 1) {
      showErrorNotification("Enter at least 1 minute of care time.", {
        title: "Minutes required",
      });
      return;
    }
    setSubmitting(true);
    try {
      // The inline editor is always editing an existing entry. Preserve
      // the original `effectiveDateTime` when the user hasn't changed
      // the date so the activity-log row keeps its original "today at
      // HH:MM" anchor; only re-anchor when the date actually moves.
      const originalIso =
        observation.effectiveDateTime ?? observation.effectivePeriod?.start;
      const originalDateString = originalIso?.slice(0, 10);
      const dateUnchanged = date === originalDateString;
      const isToday = date === todayDate();
      const effectiveDate =
        dateUnchanged && originalIso
          ? originalIso
          : isToday
            ? new Date().toISOString()
            : new Date(`${date}T12:00:00.000Z`).toISOString();
      const facade = createCareTimeFacade(medplum);
      const { observation: updated } = await facade.upsert({
        formData: {
          minutes,
          method,
          notes: notes.trim() || undefined,
          effectiveDate,
          patientId,
          patientDisplay,
          performerRoleId,
          performerDisplay,
          existingObservationId: observation.id,
          taskId: observation.focus?.[0]?.reference?.startsWith("Task/")
            ? observation.focus[0].reference.slice("Task/".length)
            : undefined,
          taskDisplay: observation.focus?.[0]?.display,
          carePlanId: observation.basedOn?.[0]?.reference?.startsWith(
            "CarePlan/",
          )
            ? observation.basedOn[0].reference.slice("CarePlan/".length)
            : undefined,
          carePlanDisplay: observation.basedOn?.[0]?.display,
          nonBillable,
        },
        existingObservation: observation,
      });
      onDirtyChange?.(false);
      onSaved?.(updated);
      notifications.show({
        color: "teal",
        title: "Care time updated",
        message: `${minutes} min recorded for ${patientDisplay}.`,
      });
    } catch (err) {
      console.error("Inline care-time edit failed:", err);
      showErrorNotification(
        err instanceof Error ? err.message : "Please try again.",
        { title: "Could not save changes" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!observation.id) return;
    setRemoving(true);
    try {
      const facade = createCareTimeFacade(medplum);
      await facade.softDelete({ observationId: observation.id, observation });
      onDirtyChange?.(false);
      onRemoved?.();
      notifications.show({
        color: "gray",
        title: "Care time removed",
        message: `${observation.valueQuantity?.value ?? 0} min marked as entered in error.`,
      });
    } catch (err) {
      console.error("Inline care-time soft-delete failed:", err);
      showErrorNotification(
        err instanceof Error ? err.message : "Please try again.",
        { title: "Could not remove entry" },
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Stack
      gap="sm"
      style={{
        flex: 1,
        minWidth: 0,
        backgroundColor: "var(--mantine-color-purple-0)",
        padding: "var(--mantine-spacing-sm)",
        borderRadius: "var(--mantine-radius-sm)",
      }}
    >
      <Group gap="sm" wrap="wrap" align="flex-end">
        <TextInput
          label="Date"
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.currentTarget.value);
            markDirty();
          }}
          disabled={submitting}
          size="xs"
          style={{ minWidth: 140 }}
        />
        <Select
          label="Activity"
          value={method}
          onChange={(v) => {
            if (!v) return;
            setMethod(v as CareTimeMethod);
            markDirty();
          }}
          data={methodOptions}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
          disabled={submitting || isAuto}
          size="xs"
          style={{ minWidth: 200 }}
        />
        <NumberInput
          label="Minutes"
          value={minutes ?? ""}
          onChange={(v) => {
            if (v === "" || v === null) {
              setMinutes(undefined);
            } else {
              const n = typeof v === "number" ? v : Number(v);
              setMinutes(Number.isFinite(n) ? n : undefined);
            }
            markDirty();
          }}
          min={1}
          max={480}
          step={1}
          decimalScale={0}
          disabled={submitting}
          size="xs"
          style={{ minWidth: 100 }}
          aria-label="Minutes"
        />
        <Group gap={4} ml="auto">
          <Tooltip label="Save changes" withArrow>
            <ActionIcon
              color="teal"
              variant="light"
              loading={submitting}
              onClick={() => {
                void handleSave();
              }}
              aria-label="Save care time edit"
            >
              <IconCheck size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Cancel" withArrow>
            <ActionIcon
              color="gray"
              variant="subtle"
              onClick={() => {
                onDirtyChange?.(false);
                onCancel();
              }}
              disabled={submitting}
              aria-label="Cancel care time edit"
            >
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <Textarea
        label="Notes"
        value={notes}
        onChange={(e) => {
          setNotes(e.currentTarget.value);
          markDirty();
        }}
        placeholder="Notes (optional)"
        autosize
        minRows={2}
        disabled={submitting}
        size="xs"
      />
      <Switch
        checked={nonBillable}
        onChange={(e) => {
          setNonBillable(e.currentTarget.checked);
          markDirty();
        }}
        label="Exclude from CoCM billing (non-billable)"
        disabled={submitting}
        size="xs"
        aria-label="Exclude from CoCM billing (non-billable)"
      />
      {observation.id ? (
        <Group justify="flex-end">
          <Anchor
            component="button"
            type="button"
            size="xs"
            c="red"
            onClick={() => {
              void handleRemove();
            }}
            disabled={removing || submitting}
            aria-label="Remove care time entry"
          >
            {removing ? "Removing…" : "Remove entry"}
          </Anchor>
        </Group>
      ) : null}
    </Stack>
  );
};
